// AppShell — Agreement Command Center chrome.
//
// Single-column shell: a 56px topbar carrying the boxed monogram (→ /overview),
// the primary areas (Overview / Sent Items / Households / Owners & properties /
// Signals), a Tools dropdown for the secondary surfaces, the ⌘K search trigger,
// and the signed-in user. The Signals item shows a live block+attention count
// (180s poll while the tab is in use, fail-soft). The lease builder relies on
// .app-main's padding — children render directly inside it, no wrappers.
//
// Agreements and Docs left the nav together (Q1): Sent Items IS both of them,
// one row per item. Their routes still resolve — they redirect (§9) — but a nav
// item is a claim that a place exists, and neither does any more.

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/Toast';
import { useTodosCount } from '@/hooks/useLeaseLifecycle';
import { usePollWhileActive } from '@/hooks/usePollWhileActive';
import { fetchSignalsSummary, type SignalsSummary } from '@/lib/commandCenterClient';
import { KeyCap } from '@/components/oc/bits';
import { CommandPalette } from '@/components/CommandPalette';
import monogramUrl from '@/design-system/assets/brand/boxed-monogram-light.svg';

interface AppShellProps {
  children: ReactNode;
}

const PRIMARY_NAV = [
  { label: 'Overview', path: '/overview' },
  // An item's Full Details page (`/items/:itemId`) belongs to Sent Items even
  // though it does not sit under its path — arriving there from a household, or
  // from an emailed link, should still light the area it is part of.
  { label: 'Sent Items', path: '/sent-items', also: ['/items', '/leases'] },
  { label: 'Households', path: '/households' },
  { label: 'Owners & properties', path: '/owners' },
  { label: 'Signals', path: '/signals' },
  { label: 'Follow-ups', path: '/follow-ups' },
] as const satisfies ReadonlyArray<{
  label: string;
  path: string;
  also?: readonly string[];
}>;

const TOOL_LINKS = [
  { label: 'Send', path: '/send' },
  { label: 'Requests', path: '/requests' },
  { label: 'To-dos', path: '/todos' },
  { label: 'Verifications', path: '/verifications' },
  { label: 'Accommodations', path: '/accommodations' },
  { label: 'Condition reports', path: '/condition-reports' },
  { label: 'HOAs', path: '/hoas' },
  { label: 'PM directory', path: '/pm-directory' },
  { label: 'Test lease', path: '/test-lease' },
] as const;

export function AppShell({ children }: AppShellProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const displayName = user?.name ?? user?.email ?? user?.preferredUsername ?? null;
  // Open came-of-age to-dos — drives the warning pill on the Tools → To-dos item
  // the few times a year it's relevant (WEL-31). 0 = no badge.
  const todosCount = useTodosCount();

  // --- Signals count (block + attention), polled every 180s, fail-soft -------
  // (An idle tab shouldn't refresh the auth session more aggressively.)
  // Only while someone is using the tab (Aurora load workstream #4): a tab left
  // open overnight used to poll /api/cc/* ~20×/hour and keep the cluster awake.
  // Paused while hidden / 15 min without input; refreshes on return.
  const [signalsSummary, setSignalsSummary] = useState<SignalsSummary | null>(null);
  const shellAliveRef = useRef(true);
  useEffect(() => {
    shellAliveRef.current = true;
    return () => {
      shellAliveRef.current = false;
    };
  }, []);
  usePollWhileActive(() => {
    fetchSignalsSummary()
      .then((s) => {
        if (shellAliveRef.current) setSignalsSummary(s);
      })
      .catch(() => {
        // Fail-soft: hide the count rather than show a stale or bogus number.
        if (shellAliveRef.current) setSignalsSummary(null);
      });
  }, 180_000);
  const signalsBlockCount = signalsSummary?.block ?? 0;
  const signalsCount = signalsBlockCount + (signalsSummary?.attention ?? 0);
  const signalsBlocked = signalsBlockCount > 0;

  // --- Tools dropdown ---------------------------------------------------------
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);
  // Close after any navigation (covers keyboard-activated links too).
  useEffect(() => {
    setToolsOpen(false);
  }, [location.pathname]);

  // --- Command palette (⌘K / Ctrl+K) -------------------------------------------
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');
  // A tool page is the current route — light the Tools trigger like the five
  // primary areas so the active-route semantics don't dead-end in the menu.
  const toolsActive = TOOL_LINKS.some(({ path }) => isActive(path));

  return (
    // Single-column grid (no sidebar) — nav lives inline in the topbar. Rows
    // pinned to the 56px topbar so the shell and header agree on the height.
    <div className="app-shell" style={{ gridTemplateColumns: '1fr', gridTemplateRows: '56px 1fr' }}>
      {/* Reset for the button-flavored nav link (Tools trigger): kill the UA
          button chrome at element+class specificity so the design system's
          .app-nav-link:hover / open-state rules still win. Tokens only. */}
      <style>{
        'button.app-nav-link{background:transparent;border:0;cursor:pointer;font-family:inherit;}' +
        'button.app-nav-link[aria-expanded="true"]{background:var(--bg-alt);color:var(--fg);}'
      }</style>
      <header className="app-topbar" style={{ height: 56, minHeight: 56 }}>
        <Link to="/overview" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img src={monogramUrl} alt="WHP" height={32} style={{ display: 'block' }} />
        </Link>
        <nav className="cluster" aria-label="Primary" style={{ gap: 'var(--space-1)' }}>
          {PRIMARY_NAV.map((item) => {
            const { label, path } = item;
            const also = 'also' in item ? item.also : undefined;
            const active = isActive(path) || (also ?? []).some(isActive);
            return (
            <Link
              key={path}
              to={path}
              className="app-nav-link"
              aria-current={active ? 'page' : undefined}
            >
              {label}
              {path === '/signals' && signalsCount > 0 && (
                <span
                  className="oc-meta"
                  style={{ whiteSpace: 'nowrap' }}
                  aria-label={`${signalsCount} open signals${
                    signalsBlockCount > 0 ? `, ${signalsBlockCount} blocking` : ''
                  }`}
                >
                  {'· '}
                  <span
                    style={{
                      fontVariantNumeric: 'var(--numerals-tabular)',
                      ...(signalsBlocked ? { color: 'var(--danger-fg)' } : {}),
                    }}
                  >
                    {signalsCount}
                  </span>
                </span>
              )}
            </Link>
            );
          })}
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className="app-nav-link"
              aria-haspopup="menu"
              aria-expanded={toolsOpen}
              aria-current={toolsActive ? 'page' : undefined}
              onClick={() => setToolsOpen((v) => !v)}
            >
              Tools{' '}
              {todosCount > 0 && (
                // Passive visibility for the To-dos badge — it must not hide
                // inside the closed menu (mirrors the menu item's pill).
                <span
                  className="pill"
                  data-tone="warning"
                  aria-label={`${todosCount} to-do${todosCount === 1 ? '' : 's'} needing attention`}
                  style={{
                    height: '18px',
                    padding: '0 var(--space-2)',
                    fontWeight: 'var(--weight-bold)',
                  }}
                >
                  {todosCount}
                </span>
              )}{' '}
              <span aria-hidden="true" style={{ fontSize: '9px', color: 'var(--fg-subtle)' }}>
                ▾
              </span>
            </button>
            {toolsOpen && (
              <div
                className="menu"
                role="menu"
                style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px' }}
              >
                {TOOL_LINKS.map(({ label, path }) => (
                  <Link
                    key={path}
                    to={path}
                    className="menu-item"
                    role="menuitem"
                    aria-current={isActive(path) ? 'page' : undefined}
                    onClick={() => setToolsOpen(false)}
                  >
                    {label}
                    {path === '/todos' && todosCount > 0 && (
                      <span
                        className="pill"
                        data-tone="warning"
                        aria-label={`${todosCount} to-do${todosCount === 1 ? '' : 's'} needing attention`}
                        style={{
                          marginLeft: 'auto',
                          height: '18px',
                          padding: '0 var(--space-2)',
                          fontWeight: 'var(--weight-bold)',
                        }}
                      >
                        {todosCount}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>
        <span className="app-topbar-fill" />
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size="sm"
          onClick={() => setPaletteOpen(true)}
        >
          Search <KeyCap>⌘K</KeyCap>
        </button>
        {user && (
          <div className="cluster" style={{ gap: 'var(--space-3)' }}>
            {displayName && (
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                {displayName}
              </span>
            )}
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              onClick={() => void logout()}
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <main className="app-main">{children}</main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Toaster />
    </div>
  );
}
